import React, { useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { SignInForm } from './SignInForm';
import { SignUpForm } from './SignUpForm';
import { GuestAccessForm } from './GuestAccessForm';
import { useNavigate } from 'react-router-dom';

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  defaultView?: 'signin' | 'signup' | 'guest';
}

export const AuthModal: React.FC<AuthModalProps> = ({ 
  isOpen, 
  onClose, 
  defaultView = 'signin' 
}) => {
  const [view, setView] = useState(defaultView);
  const navigate = useNavigate();

  const handleSuccess = () => {
    onClose();
    navigate('/?mode=dashboard&section=projects');
  };

  return (
    <Transition show={isOpen} as={React.Fragment}>
      <Dialog onClose={onClose} className="relative z-50">
        <Transition.Child
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/30" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 shadow-xl transition-all">
                <div className="flex justify-between items-center mb-4">
                  <Dialog.Title className="text-lg font-semibold text-gray-900">
                    {view === 'signin' && 'Sign In'}
                    {view === 'signup' && 'Create Account'}
                    {view === 'guest' && 'Guest Access'}
                  </Dialog.Title>
                  <button
                    onClick={onClose}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    <XMarkIcon className="w-5 h-5" />
                  </button>
                </div>

                {view === 'signin' && (
                  <SignInForm 
                    onSignUp={() => setView('signup')}
                    onGuestAccess={() => setView('guest')}
                    onSuccess={handleSuccess}
                  />
                )}
                {view === 'signup' && (
                  <SignUpForm 
                    onSignIn={() => setView('signin')}
                    onGuestAccess={() => setView('guest')}
                    onSuccess={handleSuccess}
                  />
                )}
                {view === 'guest' && (
                  <GuestAccessForm
                    onSignIn={() => setView('signin')}
                    onSignUp={() => setView('signup')}
                    onSuccess={onClose}
                  />
                )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};